/*
 * One of PayPal's buttons at checkout (components/card/order/paypal): PayPal's own gold one (x-ref="paypal") or
 * PayPal | Card (x-ref="card"), each drawn on its own (so there's no "Powered by PayPal" line), from PayPal's JS SDK,
 * loaded once per page for both.
 *
 * Pressing one asks the site for the PayPal order (Checkout::paypalCreate, made for the current total); approving it
 * has the site take the money (Checkout::paypalCapture), which opens the order as paid. The card form PayPal opens
 * inside its frame shows as a dialog: it opens with a spinner the moment the button is pressed, takes over once
 * PayPal's frame grows, and closing it (×, Escape, a click outside) draws the card button afresh in its place.
 */
Alpine.data('paypalButtons', (sdk) => ({
    failed: false,      // the SDK didn't load
    cardReady: false,   // PayPal | Card is drawn (its label shows)
    cardLoading: false, // pressed, PayPal's form not there yet
    cardOpen: false,    // PayPal's form is showing (its frame grew)

    get shown() {
        return this.cardOpen || this.cardLoading;
    },

    closeCard() {},

    // Leaving the page with the dialog up (paid, so on to the order): the page behind scrolls again
    destroy() {
        document.body.classList.remove('overflow-hidden');
    },

    async init() {
        this.$refs.card && new ResizeObserver(([entry]) => {
            this.cardOpen = entry.contentRect.height > 80;
            if (this.cardOpen) this.cardLoading = false;
        }).observe(this.$refs.card);

        window.paypalSdk ??= new Promise((resolve, reject) => {
            const script = Object.assign(document.createElement('script'), {src: sdk, async: true, onload: resolve, onerror: reject});
            document.head.append(script);
        });

        try {
            await window.paypalSdk;
        } catch {
            this.failed = true;
            return;
        }

        const paypal = window.paypal;
        const draw = (source, target) => {
            const buttons = paypal.Buttons({
                fundingSource: source,
                style: {shape: 'rect', borderRadius: 12, height: 48, color: source === paypal.FUNDING.CARD ? 'black' : 'gold'},
                createOrder: () => this.create(source === paypal.FUNDING.CARD),
                onApprove: () => this.$wire.paypalCapture(),
                onCancel: () => this.cardLoading = false,
                onError: (error) => {
                    this.cardLoading = false;
                    if (error?.message !== 'handled') Toaster.error('PayPal didn\'t finish. Nothing was charged; please try again.');
                },
            });

            if (!buttons.isEligible()) {
                target.remove();
                return {buttons, drawn: Promise.resolve()};
            }

            const drawn = buttons.render(target).then(() => source === paypal.FUNDING.CARD && (this.cardReady = true));
            return {buttons, drawn};
        };

        if (this.$refs.paypal) {
            draw(paypal.FUNDING.PAYPAL, this.$refs.paypal);
            return;
        }

        // Kept here, not on `this`: PayPal's instance mustn't be wrapped in Alpine's reactive proxy
        let card = draw(paypal.FUNDING.CARD, this.$refs.card).buttons;
        this.closeCard = async () => {
            this.cardLoading = false;
            this.cardReady = false;
            await card.close();
            const next = draw(paypal.FUNDING.CARD, this.$refs.card);
            card = next.buttons;
            // Back where the keyboard was: on the card button
            await next.drawn;
            this.$refs.card.querySelector('iframe')?.focus();
        };
    },

    /** The PayPal order's id for PayPal's window; the card form's dialog opens at once, with a spinner, while it's made. */
    async create(card) {
        if (card) this.cardLoading = true;

        const id = await this.$wire.paypalCreate();
        if (!id) {
            this.cardLoading = false;
            throw new Error('handled');   // the reason is already on screen
        }

        return id;
    },

    /** Open, the card form goes in the top layer (a popover), over everything: the sticky summary would keep it under the navbar. */
    layer(el) {
        if (this.shown) {
            el.setAttribute('popover', 'manual');
            el.showPopover();
        } else if (el.hasAttribute('popover')) {
            el.hidePopover();
            el.removeAttribute('popover');
        }
    },
}));
